/**
 * Something in the workspace changed: said once, heard by every list.
 *
 * The sidebar and the pane beside it each load their own lists, and each can create,
 * rename, move and delete. So a folder made in the tree never appeared in the
 * Documents list, and a document deleted from the list stayed in the tree until the
 * next click. Every change calls `changed()`, and everything that lists the workspace's
 * contents reloads on `useChanges()`.
 *
 * One counter rather than an event per kind of thing. A handful of small reads after a
 * rename costs less than plumbing that decides which lists a rename could affect, and
 * that plumbing is exactly what the one forgotten screen gets wrong.
 */
import { useSyncExternalStore } from "react";

let version = 0;
const listeners = new Set<() => void>();

/** Tells every list that the workspace's contents are different now. */
export function changed(): void {
  version++;
  for (const listener of listeners) listener();
}

/** A number that goes up whenever the workspace changes. Put it in an effect's dependencies. */
export function useChanges(): number {
  return useSyncExternalStore(subscribe, () => version, () => 0);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}
